// Achievements system configuration

export interface Achievement {
  id: string
  name: string
  description: string
  emoji: string
  category: AchievementCategory
  requirement: AchievementRequirement
  /** Medal tier shown in the vitrine and unlock banner */
  tier: AchievementTier
  reward?: {
    money?: number
    reputation?: number
  }
  isSecret?: boolean
}

export type AchievementTier = 'bronze' | 'silver' | 'gold'

export type AchievementCategory = 'construction' | 'economy' | 'customers' | 'staff' | 'time' | 'special'

export interface AchievementRequirement {
  type: RequirementType
  value: number
}

export type RequirementType =
  | 'courts_built'
  | 'buildings_built'
  | 'staff_hired'
  | 'money_earned'
  | 'money_spent'
  | 'customers_served'
  | 'days_played'
  | 'reputation_reached'
  | 'balance_reached'
  | 'tournaments_hosted'
  | 'players_signed'
  | 'league_promotions'
  | 'league_titles'

export const ACHIEVEMENTS: Achievement[] = [
  // Construction
  {
    id: 'first_court',
    tier: 'bronze',
    name: 'Primera Pista',
    description: 'Construye tu primera pista de pádel',
    emoji: '🎾',
    category: 'construction',
    requirement: { type: 'courts_built', value: 1 },
    reward: { money: 500 },
  },
  {
    id: 'expanding',
    tier: 'silver',
    name: 'En Expansión',
    description: 'Construye 3 pistas',
    emoji: '🏗️',
    category: 'construction',
    requirement: { type: 'courts_built', value: 3 },
    reward: { money: 1000 },
  },
  {
    id: 'court_master',
    tier: 'gold',
    name: 'Master Constructor',
    description: 'Construye 6 pistas',
    emoji: '🏟️',
    category: 'construction',
    requirement: { type: 'courts_built', value: 6 },
    reward: { money: 2500, reputation: 0.5 },
  },
  {
    id: 'first_building',
    tier: 'bronze',
    name: 'Primer Edificio',
    description: 'Construye tu primer edificio',
    emoji: '🏢',
    category: 'construction',
    requirement: { type: 'buildings_built', value: 1 },
    reward: { money: 300 },
  },
  {
    id: 'full_facilities',
    tier: 'gold',
    name: 'Club Completo',
    description: 'Construye los 4 tipos de edificios',
    emoji: '🌟',
    category: 'construction',
    requirement: { type: 'buildings_built', value: 4 },
    reward: { money: 3000, reputation: 1 },
  },
  
  // Economy
  {
    id: 'first_income',
    tier: 'bronze',
    name: 'Primer Ingreso',
    description: 'Gana tu primer €100',
    emoji: '💰',
    category: 'economy',
    requirement: { type: 'money_earned', value: 100 },
  },
  {
    id: 'profitable',
    tier: 'silver',
    name: 'Rentable',
    description: 'Acumula €5.000 en ingresos',
    emoji: '📈',
    category: 'economy',
    requirement: { type: 'money_earned', value: 5000 },
    reward: { money: 500 },
  },
  {
    id: 'mogul',
    tier: 'gold',
    name: 'Magnate del Pádel',
    description: 'Acumula €50.000 en ingresos',
    emoji: '💎',
    category: 'economy',
    requirement: { type: 'money_earned', value: 50000 },
    reward: { money: 5000, reputation: 0.5 },
  },
  {
    id: 'millionaire',
    tier: 'gold',
    name: 'Millonario',
    description: 'Alcanza €100.000 de balance',
    emoji: '🤑',
    category: 'economy',
    requirement: { type: 'balance_reached', value: 100000 },
    reward: { reputation: 1 },
    isSecret: true,
  },
  
  // Customers
  {
    id: 'first_customer',
    tier: 'bronze',
    name: 'Primer Cliente',
    description: 'Atiende a tu primer cliente',
    emoji: '👋',
    category: 'customers',
    requirement: { type: 'customers_served', value: 1 },
  },
  {
    id: 'popular_club',
    tier: 'silver',
    name: 'Club Popular',
    description: 'Atiende a 100 clientes',
    emoji: '🎉',
    category: 'customers',
    requirement: { type: 'customers_served', value: 100 },
    reward: { money: 1000 },
  },
  {
    id: 'community_hub',
    tier: 'gold',
    name: 'Centro Comunitario',
    description: 'Atiende a 500 clientes',
    emoji: '🏆',
    category: 'customers',
    requirement: { type: 'customers_served', value: 500 },
    reward: { money: 5000, reputation: 0.5 },
  },
  
  // Staff
  {
    id: 'first_hire',
    tier: 'bronze',
    name: 'Primera Contratación',
    description: 'Contrata a tu primer empleado',
    emoji: '👤',
    category: 'staff',
    requirement: { type: 'staff_hired', value: 1 },
    reward: { money: 200 },
  },
  {
    id: 'growing_team',
    tier: 'silver',
    name: 'Equipo en Crecimiento',
    description: 'Contrata 3 empleados',
    emoji: '👥',
    category: 'staff',
    requirement: { type: 'staff_hired', value: 3 },
    reward: { money: 500 },
  },
  {
    id: 'full_staff',
    tier: 'gold',
    name: 'Plantilla Completa',
    description: 'Tiene 6 empleados trabajando',
    emoji: '🏢',
    category: 'staff',
    requirement: { type: 'staff_hired', value: 6 },
    reward: { money: 2000, reputation: 0.3 },
  },
  
  // Time
  {
    id: 'first_week',
    tier: 'bronze',
    name: 'Primera Semana',
    description: 'Sobrevive tu primera semana',
    emoji: '📅',
    category: 'time',
    requirement: { type: 'days_played', value: 7 },
    reward: { money: 500 },
  },
  {
    id: 'first_month',
    tier: 'silver',
    name: 'Primer Mes',
    description: 'Completa tu primer mes de operación',
    emoji: '🗓️',
    category: 'time',
    requirement: { type: 'days_played', value: 28 },
    reward: { money: 1000 },
  },
  {
    id: 'veteran',
    tier: 'gold',
    name: 'Veterano',
    description: 'Juega durante 100 días',
    emoji: '⭐',
    category: 'time',
    requirement: { type: 'days_played', value: 100 },
    reward: { money: 5000, reputation: 1 },
  },
  
  // Reputation
  {
    id: 'rising_star',
    tier: 'silver',
    name: 'Estrella Emergente',
    description: 'Alcanza 4 estrellas de reputación',
    emoji: '⭐',
    category: 'special',
    requirement: { type: 'reputation_reached', value: 4 },
    reward: { money: 2000 },
  },
  {
    id: 'top_club',
    tier: 'gold',
    name: 'Club de Élite',
    description: 'Alcanza 5 estrellas de reputación',
    emoji: '🌟',
    category: 'special',
    requirement: { type: 'reputation_reached', value: 5 },
    reward: { money: 10000 },
    isSecret: true,
  },

  // Competition
  {
    id: 'tournament_host',
    tier: 'bronze',
    name: 'Anfitrión',
    description: 'Organiza tu primer torneo',
    emoji: '🏆',
    category: 'special',
    requirement: { type: 'tournaments_hosted', value: 1 },
    reward: { money: 500 },
  },
  {
    id: 'tournament_circuit',
    tier: 'gold',
    name: 'Parada del Circuito',
    description: 'Organiza 5 torneos',
    emoji: '🎪',
    category: 'special',
    requirement: { type: 'tournaments_hosted', value: 5 },
    reward: { money: 3000, reputation: 0.3 },
  },
  {
    id: 'first_signing',
    tier: 'bronze',
    name: 'Primer Fichaje',
    description: 'Ficha a tu primer jugador',
    emoji: '✍️',
    category: 'staff',
    requirement: { type: 'players_signed', value: 1 },
    reward: { money: 300 },
  },
  {
    id: 'full_roster',
    tier: 'silver',
    name: 'Equipazo',
    description: 'Ten 6 jugadores en plantilla',
    emoji: '🤝',
    category: 'staff',
    requirement: { type: 'players_signed', value: 6 },
    reward: { money: 2000 },
  },
  {
    id: 'first_promotion',
    tier: 'silver',
    name: 'Ascenso',
    description: 'Asciende de división en la liga',
    emoji: '🚀',
    category: 'special',
    requirement: { type: 'league_promotions', value: 1 },
    reward: { money: 2000, reputation: 0.3 },
  },
  {
    id: 'league_champion',
    tier: 'gold',
    name: 'Campeones',
    description: 'Gana un título de liga',
    emoji: '👑',
    category: 'special',
    requirement: { type: 'league_titles', value: 1 },
    reward: { money: 5000, reputation: 0.5 },
  },
]

// Get achievement by ID
export function getAchievement(id: string): Achievement | undefined {
  return ACHIEVEMENTS.find(a => a.id === id)
}

// Get achievements by category
export function getAchievementsByCategory(category: AchievementCategory): Achievement[] {
  return ACHIEVEMENTS.filter(a => a.category === category)
}

/** Current stat value backing a requirement type */
export function getStatForRequirement(type: RequirementType, stats: GameStats): number {
  switch (type) {
    case 'courts_built': return stats.courtsBuilt
    case 'buildings_built': return stats.buildingsBuilt
    case 'staff_hired': return stats.staffHired
    case 'money_earned': return stats.totalMoneyEarned
    case 'money_spent': return stats.totalMoneySpent
    case 'customers_served': return stats.customersServed
    case 'days_played': return stats.daysPlayed
    case 'reputation_reached': return stats.maxReputation
    case 'balance_reached': return stats.maxBalance
    case 'tournaments_hosted': return stats.tournamentsHosted
    case 'players_signed': return stats.playersSigned
    case 'league_promotions': return stats.leaguePromotions
    case 'league_titles': return stats.leagueTitles
    default: return 0
  }
}

// Check if requirement is met
export function checkRequirement(
  requirement: AchievementRequirement,
  stats: GameStats
): boolean {
  return getStatForRequirement(requirement.type, stats) >= requirement.value
}

// Game statistics interface
export interface GameStats {
  courtsBuilt: number
  buildingsBuilt: number
  staffHired: number
  totalMoneyEarned: number
  totalMoneySpent: number
  customersServed: number
  daysPlayed: number
  maxReputation: number
  maxBalance: number
  eventsResolved: number
  tournamentsHosted: number
  playersSigned: number // max roster size reached
  leaguePromotions: number
  leagueTitles: number
}

export const initialStats: GameStats = {
  courtsBuilt: 0,
  buildingsBuilt: 0,
  staffHired: 0,
  totalMoneyEarned: 0,
  totalMoneySpent: 0,
  customersServed: 0,
  daysPlayed: 0,
  maxReputation: 3,
  maxBalance: 10000,
  eventsResolved: 0,
  tournamentsHosted: 0,
  playersSigned: 0,
  leaguePromotions: 0,
  leagueTitles: 0,
}
